// src/utils/dateUtils.ts
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

/**
 * Converts any date-like value to an ISO string for storage
 * 
 * @param dateValue - The date value to convert (Date, Timestamp, ISO string, etc.)
 * @returns ISO string representation of the date
 */
export const toISOString = (dateValue: any): string => {
  if (!dateValue) {
    return new Date().toISOString();
  }
  
  // If already a string, check if it's a valid ISO string
  if (typeof dateValue === 'string') {
    try {
      // Check if it's a valid date string
      const parsed = new Date(dateValue);
      if (!isNaN(parsed.getTime())) {
        return dateValue;
      }
    } catch (error) {
      // If parsing fails, return current date
      console.error('Invalid date string:', dateValue);
      return new Date().toISOString();
    }
  }
  
  // If it's a Date object
  if (dateValue instanceof Date) {
    return dateValue.toISOString();
  }
  
  // If it's a Firestore Timestamp
  if (dateValue instanceof firebase.firestore.Timestamp) {
    return dateValue.toDate().toISOString();
  }
  
  // If we get here, use the current date
  return new Date().toISOString();
};

/**
 * Formats an ISO date string for display
 * 
 * @param isoString - ISO date string to format
 * @param format - Optional format ('short', 'long', etc.)
 * @returns Formatted date string
 */
export const formatDate = (isoString: string, format: 'short' | 'long' | 'datetime' = 'short'): string => {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    switch (format) {
      case 'long':
        return date.toLocaleDateString(undefined, { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      case 'datetime':
        return date.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      case 'short':
      default:
        return date.toLocaleDateString();
    }
  } catch (error) {
    console.error('Error formatting date:', isoString, error);
    return 'Invalid date';
  }
};

/**
 * Normalizes a job object's date properties to ISO strings
 * 
 * @param job - The job object to normalize
 * @returns The job with normalized date properties
 */
export const normalizeJobDates = <T extends { dtCreated: any, matchedAt?: any }>(job: T): T => {
  return {
    ...job,
    dtCreated: toISOString(job.dtCreated),
    ...(job.matchedAt !== undefined ? { matchedAt: toISOString(job.matchedAt) } : {})
  };
};